import { Textarea } from "@/modules/shadcn/ui/textarea";
import { LABEL_DESCRIPTION_MAX, Label, LabelDescriptions } from "@repo/schema";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  GenerateDescriptionButton,
  type GenerateLabelDescription,
} from "../GenerateDescriptionButton";
import {
  DESCRIPTION_ARIA_LABEL,
  DESCRIPTION_GENERATE_FAILED,
  DESCRIPTION_PLACEHOLDER,
  DESCRIPTIONS_BUSY,
  DESCRIPTIONS_FOOTER,
  DESCRIPTIONS_HELP,
  DESCRIPTIONS_NO_LABELS,
  DESCRIPTIONS_TITLE,
} from "./copy";
import { SettingsSection } from "./SettingsSection";
import { apiErrorMessage } from "@/core/api";

interface LabelDescriptionsSectionProps {
  labels: readonly Label[];
  descriptions: LabelDescriptions;
  onChange: (labelId: number, value: string) => void;
  disabled?: boolean;
  /** Absent hides the ✨ on every row. The result is a draft until Save. */
  onGenerate?: GenerateLabelDescription;
}

/**
 * One description per project label — the same text as `label.description`,
 * so what is typed here is what the Edit Project dialog shows, and vice versa.
 * A label left blank is simply not looked for.
 */
export const LabelDescriptionsSection = ({
  labels,
  descriptions,
  onChange,
  disabled,
  onGenerate,
}: LabelDescriptionsSectionProps) => {
  const { t } = useTranslation(["label", "common"]);
  // Per row: several labels may be generating at once.
  const [pending, setPending] = useState<ReadonlySet<number>>(() => new Set());

  const setRowPending = (labelId: number, on: boolean) =>
    setPending((prev) => {
      const next = new Set(prev);
      if (on) next.add(labelId);
      else next.delete(labelId);
      return next;
    });

  const generate = async (label: Label) => {
    if (!onGenerate) return;
    setRowPending(label.id, true);
    try {
      const text = await onGenerate({
        labelId: label.id,
        name: label.name,
        currentDescription: descriptions[String(label.id)] ?? "",
      });
      onChange(label.id, text);
    } catch (err) {
      toast.error(apiErrorMessage(err, t(DESCRIPTION_GENERATE_FAILED)));
    } finally {
      setRowPending(label.id, false);
    }
  };

  return (
    <SettingsSection
      title={t(DESCRIPTIONS_TITLE)}
      help={t(DESCRIPTIONS_HELP)}
      footerHelp={labels.length > 0 ? t(DESCRIPTIONS_FOOTER) : undefined}
    >
      {labels.length === 0 ? (
        <p className="rounded-md border border-dashed border-black/10 bg-black/[0.03] px-3 py-2 text-sm text-muted-foreground">
          {t(DESCRIPTIONS_NO_LABELS)}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {labels.map((l) => (
            <div key={l.id} className="flex items-start gap-3">
              <span
                className="mt-3.5 size-2 shrink-0 rounded-full"
                style={{ backgroundColor: l.color }}
                aria-hidden
              />
              <span
                className="w-[170px] shrink-0 truncate pt-2 text-sm leading-5"
                title={l.name}
              >
                {l.name}
              </span>
              {/* Grows with its text (the primitive is field-sizing-content):
                  one line for a short description, up to ~6 before it scrolls. */}
              <Textarea
                maxLength={LABEL_DESCRIPTION_MAX}
                disabled={disabled}
                readOnly={pending.has(l.id)}
                aria-label={t(DESCRIPTION_ARIA_LABEL, { name: l.name })}
                placeholder={t(DESCRIPTION_PLACEHOLDER, { name: l.name.toLowerCase() })}
                value={descriptions[String(l.id)] ?? ""}
                onChange={(e) => onChange(l.id, e.target.value)}
                className="min-h-9 max-h-32 resize-none rounded-md border-0 bg-black/[0.04] px-3 py-2 text-sm leading-5 shadow-none"
              />
              {onGenerate && (
                <GenerateDescriptionButton
                  iconOnly
                  pending={pending.has(l.id)}
                  disabledReason={disabled ? t(DESCRIPTIONS_BUSY) : null}
                  onClick={() => void generate(l)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </SettingsSection>
  );
};
